/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useState } from "react";
import { mediaReconciliationService } from "../../../../../service/mediaReconciliationServices";
import { useTranslation } from "react-i18next";
import {
	BatchReconciliationRequest,
	ReconciliationPayload,
	ReconContainerPostRes,
} from "../StartReconciliation.types";

interface Props {
	totalVariance: number;
	postData: ReconciliationPayload[];
	batchName: string;
	lot_code: string;
	unit: string;
	mediaType: string;
	batchId: string;
	toggleModal: () => void;
	handlePostSuccess: () => void;
	showToastMessage: (
		message: string,
		type: string
	) => void;
}

const SReconcileConfirmModal: React.FC<Props> = ({
	totalVariance,
	postData,
	batchName,
	lot_code,
	unit,
	toggleModal,
	handlePostSuccess,
	batchId,
	mediaType,
	showToastMessage
}) => {
	const { t } = useTranslation();
	const [isPosting, setIsPosting] = useState<boolean>(false);
	const [postWarning, setPostWarning] = useState<string | null>(null);

	const handleContainerPost = async () => {
		const payload: BatchReconciliationRequest = {
			batch_id: Number(batchId),
			reconciliations: postData,
		};
		try {
			const response: ReconContainerPostRes = await mediaReconciliationService.postReconContainer(payload);
			if (response.data.summary.total_unable_to_adjust > 0) {
				showToastMessage(
					response.message ||
					"Some containers could not be adjusted.",
					"warning"
				);
				setIsPosting(false);
				toggleModal();
				handlePostSuccess();
				return;
			}
			showToastMessage(
				response.message,
				"success"
			);
			toggleModal();
			setTimeout(() => {
				handlePostSuccess();
			}, 2500);
		} catch (err: any) {
			console.log("Error occured: ", err);
			showToastMessage(
				err.response?.data?.message ??
				"Something went wrong.",
				"error"
			);
			setTimeout(() => {
				toggleModal();
			}, 2500);
			setIsPosting(false);
		}
	};




	const handlePowderPost = async () => {
		const payload: BatchReconciliationRequest = {
			batch_id: Number(batchId),
			reconciliations: postData,
		};
		try {
			console.log("Container Payload: ", payload);
			const success = await mediaReconciliationService.postReconPowder(payload);
			if (success) {
				showToastMessage(
					"Reconciliation completed successfully.",
					"success"
				);
				toggleModal();
				setTimeout(() => {
					handlePostSuccess();
				}, 2500);
			}
		} catch (err: any) {
			console.error(err);
			showToastMessage(
				err.response?.data?.message ??
				"Something went wrong.",
				"error"
			);
			setTimeout(() => {
				toggleModal();
			}, 2500);
			setIsPosting(false);
		}
	};



	const handleSolutionPost = async () => {
		const payload: BatchReconciliationRequest = {
			batch_id: Number(batchId),
			reconciliations: postData,
		};
		try {
			const success = await mediaReconciliationService.postReconSolution(payload);
			if (success) {
				showToastMessage(
					"Reconciliation completed successfully.",
					"success"
				);

				toggleModal();

				setTimeout(() => {
					handlePostSuccess();
				}, 2500);
			}
		} catch (err: any) {
			console.error(err);
			showToastMessage(
				err.response?.data?.message ??
				"Something went wrong.",
				"error"
			);
			setTimeout(() => {
				toggleModal();
			}, 2500);
			setIsPosting(false);
		}
	};


	// // ✅ Just evaluate it directly, no arrow function
	// const isContainerPositiveVariance: boolean =
	// 	mediaType === "container" && totalVariance > 0;

	const handlePost = async () => {
		if (!batchId) return;
		switch (mediaType) {
			case "powder":
				await handlePowderPost();
				break;
			case "solution":
				await handleSolutionPost();
				break;
			case "container":
				await handleContainerPost();
				break;
			default:
				break;
		}
		setIsPosting(false);
	};
	return (
		<div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
			<div
				className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm"
				onClick={() => !isPosting && toggleModal()}
			></div>
			<div className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative overflow-hidden">
				<div className="bg-[#2D3E50] px-6 py-4 flex items-center gap-3">
					<span className="material-symbols-outlined text-white">
						warning
					</span>
					<h3 className="text-sm font-black text-white tracking-widest uppercase">
						{t(
							"mediaReconcile.physicalReconciliation.confirmReconciliation",
						)}
					</h3>
				</div>

				<div className="p-8">
					<div className="w-16 h-16 bg-orange-50 rounded-full flex items-center justify-center mx-auto mb-6">
						<span className="material-symbols-outlined text-3xl text-orange-500">
							inventory
						</span>
					</div>

					<div className="text-center space-y-3 mb-8">
						<p className="text-lg font-black text-slate-800">
							{t(
								"mediaReconcile.physicalReconciliation.finalizeInventoryAdjustment",
							)}
						</p>
						<p className="text-xs text-slate-500 leading-relaxed">
							{t("mediaReconcile.physicalReconciliation.youAreAboutTo")}{" "}
							<span className="font-bold text-slate-700">
								Lot #{batchName}
								{lot_code}
							</span>
							.{" "}
							{t(
								"mediaReconcile.physicalReconciliation.stockAdjustmentMsg",
							)}{" "}
							<span
								className={`font-bold ${totalVariance < 0 ? "text-red-500" : "text-emerald-500"}`}
							>
								{totalVariance.toFixed(2)} {unit}
							</span>
							.
						</p>
						<div className="p-3 bg-slate-50 rounded-lg border border-slate-100 flex items-center justify-center gap-2">
							<span className="material-symbols-outlined text-slate-400 text-sm">
								lock_person
							</span>
							<span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
								{t(
									"mediaReconcile.physicalReconciliation.actionIsPermanent",
								)}
							</span>
						</div>
					</div>
					{postWarning && (
						<div className="mb-4 p-3 bg-amber-50 rounded-lg border border-amber-100 flex items-center gap-2">
							<span className="material-symbols-outlined text-amber-400 text-sm shrink-0">
								warning
							</span>
							<span className="text-[10px] font-bold text-amber-600 uppercase tracking-widest leading-relaxed">
								{postWarning}
							</span>
						</div>
					)}

					{/* Positive variance error message */}
					{/* {isContainerPositiveVariance && (
						<div className="mb-4 p-3 bg-red-50 rounded-lg border border-red-100 flex items-center gap-2">
							<span className="material-symbols-outlined text-red-400 text-sm">
								error
							</span>
							<span className="text-[10px] font-bold text-red-400 uppercase tracking-widest">
								{t(
									"mediaReconcile.physicalReconciliation.positiveContainerVarianceNotAllowed",
								)}
							</span>
						</div>
					)} */}

					<div className="flex gap-4">
						<button
							onClick={toggleModal}
							disabled={isPosting}
							className="flex-1 py-3 text-xs font-black uppercase tracking-widest text-slate-500 hover:bg-slate-50 rounded-xl transition-all border border-slate-200"
						>
							{t("mediaReconcile.physicalReconciliation.cancel")}
						</button>
						<button
							onClick={() => {
								setIsPosting(true);
								handlePost();
							}}

							// disabled={
							// 	isPosting ||
							// 	postData.length === 0 ||
							// 	isContainerPositiveVariance
							// }

							disabled={isPosting}
							className="flex-1 py-3 bg-[#FF7043] text-white text-xs font-black uppercase tracking-widest rounded-xl shadow-lg shadow-orange-500/20 hover:brightness-110 active:scale-95 transition-all flex items-center justify-center gap-2 border-none"
						>
							{isPosting ? (
								<>
									<span className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></span>
									{t("mediaReconcile.physicalReconciliation.posting")}
								</>
							) : (
								<>
									{t(
										"mediaReconcile.physicalReconciliation.confirmAndPost",
									)}
								</>
							)}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default SReconcileConfirmModal;
